import { FC, use } from "react";
import { Content, isFilled } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import Link from "next/link";
import { appLocales, Locales } from "@/lib/i18n";
import Bento from "@/components/molecules/bento";
import { __, getPermalink } from "@/lib/i18n/functions";
import { getAllByIDs } from "@/lib/prismic";
import { InspirationDocument } from "@/prismicio-types";

export type InspirationGridProps = SliceComponentProps<Content.InspirationGridSlice> & {
  context?: { locale?: Locales };
};

const groupInspirations = (inspirations: InspirationDocument[]) => {
  const grouped: InspirationDocument[][] = [];
  for (let i = 0; i < inspirations.length; i += 4) {
    grouped.push(inspirations.slice(i, i + 4));
  }
  return grouped;
};

const renderBentoItem = (item: InspirationDocument, variant: "lg" | "md" | "sm", index: number, locale: Locales) => {
  const validCategory = item.data.categories.find(
    (cat) => isFilled.contentRelationship(cat.inspirationCategory) && "slug" in cat.inspirationCategory
  );

  if (!validCategory || !("slug" in validCategory.inspirationCategory)) return null;

  const slug = validCategory.inspirationCategory.slug;

  return (
    <Bento
      key={`${variant}-${index}`}
      creditState={"default"}
      variant={variant}
      locale={locale}
      link={`${getPermalink(locale, "inspiration_archive")}/${slug}#${item.uid}`}
      image={{
        src: item.data.featured_image.url || "",
        alt: item.data.featured_image.alt || (item.data.title as string),
      }}
      tag={__("See used items", locale)}
      priority={index === 0}
    />
  );
};

const InspirationGrid: FC<InspirationGridProps> = ({ slice, context }) => {
  const locale = context?.locale || appLocales.defaultLocale;
  const { title, link_label, inspirations } = slice.primary;

  const validInspirations = inspirations.map((item) => item.inspiration).filter(isFilled.contentRelationship);

  const inspirationsData = use(
    getAllByIDs(validInspirations.map((rel) => rel.id))
  ) as unknown as Content.InspirationDocument[];

  if (!inspirationsData || inspirationsData.length === 0) return null;

  const groupedInspirations = groupInspirations(inspirationsData); // Group into sets of 4 to display in a grid layout

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      aria-labelledby="inspirations-grid"
      className="container space-y-7.5"
    >
      <header
        aria-labelledby="bento-grid"
        className="container flex flex-col items-center justify-center gap-3 px-4 md:flex-row md:justify-between md:px-7.5"
      >
        <h2 id="bento-grid" className="font-cabinet-grotesk text-center text-3xl font-medium">
          {title}
        </h2>
        <Link
          className="text-dark-primary focus-visible:ring-dark-primary/70 rounded-sm text-base font-medium underline focus-visible:ring-[3px] focus-visible:ring-offset-3 focus-visible:outline-none md:uppercase"
          href={getPermalink(locale, "inspiration_archive")}
        >
          {link_label}
        </Link>
      </header>

      <div className="container px-4 md:px-7.5">
        {groupedInspirations.map((group, groupIndex) => (
          <div key={groupIndex} className="mb-2.5 grid grid-cols-1 gap-2.5 xl:grid-cols-2">
            {/* First column - Large variant */}
            {group[0] && renderBentoItem(group[0], "lg", groupIndex * 4, locale)}

            {/* Second column - Medium + Small variants */}
            <div className="grid gap-2.5">
              {/* First row - Medium variant */}
              {group[1] && renderBentoItem(group[1], "md", groupIndex * 4 + 1, locale)}

              {/* Second row - Two small variants side by side */}
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                {group[2] && renderBentoItem(group[2], "sm", groupIndex * 4 + 2, locale)}
                {group[3] && renderBentoItem(group[3], "sm", groupIndex * 4 + 3, locale)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default InspirationGrid;
